import type Icon from 'ol/style/Icon';
import type { PlanningWellVariant } from '../core/types';
import { canvasIcon, HALO } from './symbolCanvas';

/**
 * V1.9.3 point symbols of the field and route objects, drawn in code (offline, sharp in the PNG export, no files):
 * - «Punto GPS / Ubicación»: a flat location pin whose tip is exactly on the point.
 * - «Finca / Hacienda»: a small colonial hacienda (tiled roof, porch, tree) over crop rows; the position of the
 *   property, never its boundary.
 * - «Destino / Punto final»: a chequered finish flag on a pole whose foot is exactly on the point.
 * The object's colour is the pin, the roof and the pole. A light halo under every silhouette keeps them legible on
 * satellite imagery without a plate or an exaggerated 3D effect.
 */

type Draw = (context: CanvasRenderingContext2D, u: (value: number) => number) => void;
const INK = '#1b2227';

/** Sizes at 100 % in CSS pixels (render scale 1), and where the geometry sits on each drawing (fractions). */
export const LOCATION_SYMBOLS = {
  'gps-point': { width: 24, height: 34, anchor: [0.5, 31 / 34] },
  farm: { width: 30, height: 28, anchor: [0.5, 13 / 28] },
  destination: { width: 28, height: 38, anchor: [7 / 28, 32 / 38] },
} as const;
export type LocationSymbolId = keyof typeof LOCATION_SYMBOLS;

/** Half of the symbol's height above/below the point, and how far under the point its label may start. */
export const locationSymbolMetrics = (id: LocationSymbolId): { readonly above: number; readonly below: number } => {
  const { height, anchor } = LOCATION_SYMBOLS[id];
  return { above: height * anchor[1], below: height * (1 - anchor[1]) };
};

function pinPath(context: CanvasRenderingContext2D, u: (value: number) => number, cx: number, cy: number, r: number, tipY: number): void {
  context.beginPath(); context.moveTo(u(cx), u(tipY));
  context.bezierCurveTo(u(cx - r * 0.3), u(tipY - r * 1.15), u(cx - r), u(cy + r * 0.62), u(cx - r), u(cy));
  context.arc(u(cx), u(cy), u(r), Math.PI, 0);
  context.bezierCurveTo(u(cx + r), u(cy + r * 0.62), u(cx + r * 0.3), u(tipY - r * 1.15), u(cx), u(tipY));
  context.closePath();
}

const drawPin = (color: string): Draw => (context, u) => {
  context.lineJoin = 'round';
  pinPath(context, u, 12, 11, 9.4, 31); context.strokeStyle = HALO; context.lineWidth = u(3.2); context.stroke();
  pinPath(context, u, 12, 11, 9.4, 31); context.fillStyle = color; context.fill();
  context.strokeStyle = 'rgba(0,0,0,0.38)'; context.lineWidth = u(0.9); context.stroke();
  // Soft highlight on the left, a white ring and a dark centre: reads as a location at 14 px.
  pinPath(context, u, 12, 11, 9.4, 31); context.save(); context.clip();
  context.fillStyle = 'rgba(255,255,255,0.18)'; context.beginPath(); context.ellipse(u(8.6), u(7.4), u(3.2), u(5.4), -0.5, 0, Math.PI * 2); context.fill(); context.restore();
  context.beginPath(); context.arc(u(12), u(11), u(4.3), 0, Math.PI * 2); context.fillStyle = '#ffffff'; context.fill();
  context.beginPath(); context.arc(u(12), u(11), u(1.9), 0, Math.PI * 2); context.fillStyle = INK; context.fill();
};

const drawFarm = (color: string, variant: PlanningWellVariant): Draw => (context, u) => {
  const rows = variant !== 'plain';
  /** One pass draws the silhouette (halo: white thick stroke), the other the painted shapes. */
  const shapes = (halo: boolean): void => {
    const paint = (fill: string | null, stroke: string | null, width = 0.7): void => {
      if (halo) { context.lineJoin = 'round'; context.strokeStyle = HALO; context.lineWidth = u(3.4); context.stroke(); context.fillStyle = HALO; context.fill(); return; }
      if (fill) { context.fillStyle = fill; context.fill(); }
      if (stroke) { context.strokeStyle = stroke; context.lineWidth = u(width); context.stroke(); }
    };
    if (rows) {
      // Ground with converging crop rows.
      context.beginPath(); context.moveTo(u(1), u(25)); context.lineTo(u(29), u(25)); context.lineTo(u(23.5), u(19.5)); context.lineTo(u(6.5), u(19.5)); context.closePath(); paint('#8bbf4a', null);
    }
    // Walls and a three-arch porch.
    context.beginPath(); context.rect(u(8), u(11.5), u(14), u(8.5)); paint('#f6efdf', '#6a5a43');
    // Hip roof of clay tiles.
    context.beginPath(); context.moveTo(u(5.8), u(12.2)); context.lineTo(u(9.6), u(6)); context.lineTo(u(20.4), u(6)); context.lineTo(u(24.2), u(12.2)); context.closePath(); paint(color, 'rgba(0,0,0,0.42)', 0.8);
    // Tree on the left.
    context.beginPath(); context.arc(u(4.3), u(14.2), u(3.6), 0, Math.PI * 2); paint('#2f7a32', '#1f5322');
    if (halo) { context.beginPath(); context.rect(u(3.6), u(16), u(1.4), u(5)); paint(null, null); }
  };
  shapes(true); shapes(false);
  if (rows) {
    context.strokeStyle = '#4f8a2b'; context.lineWidth = u(0.8); context.lineCap = 'round';
    [[10, 25, 12.6, 19.7], [14, 25, 14.2, 19.7], [18, 25, 15.8, 19.7], [22, 25, 17.4, 19.7], [6, 25, 11, 19.7]].forEach(([x1, y1, x2, y2]) => { context.beginPath(); context.moveTo(u(x1!), u(y1!)); context.lineTo(u(x2!), u(y2!)); context.stroke(); });
  }
  // Roof tiles, porch arches, windows and the tree trunk.
  context.strokeStyle = 'rgba(0,0,0,0.3)'; context.lineWidth = u(0.6);
  [8.2, 10.4].forEach((y) => { context.beginPath(); context.moveTo(u(7.4 + (8.2 - y) * 0.3), u(y)); context.lineTo(u(22.6 - (8.2 - y) * 0.3), u(y)); context.stroke(); });
  context.fillStyle = '#6b4a2a';
  [[9.4, 3.3], [13.35, 3.3], [17.3, 3.3]].forEach(([x, w]) => { context.beginPath(); context.moveTo(u(x!), u(20)); context.lineTo(u(x!), u(15.6)); context.arc(u(x! + w! / 2), u(15.6), u(w! / 2), Math.PI, 0); context.lineTo(u(x! + w!), u(20)); context.closePath(); context.fill(); });
  context.fillStyle = '#5a3d22'; context.fillRect(u(3.6), u(16.6), u(1.4), u(4.2));
};

const drawFlag = (color: string): Draw => (context, u) => {
  context.lineJoin = 'round'; context.lineCap = 'round';
  const flag = (): void => { context.beginPath(); context.moveTo(u(8), u(4)); context.bezierCurveTo(u(12), u(2), u(16), u(7), u(26), u(4.5)); context.lineTo(u(26), u(17.5)); context.bezierCurveTo(u(16), u(20), u(12), u(15), u(8), u(17)); context.closePath(); };
  // Halo: pole, flag and foot.
  context.strokeStyle = HALO; context.lineWidth = u(4.4);
  context.beginPath(); context.moveTo(u(7), u(32)); context.lineTo(u(7), u(2.5)); context.stroke();
  flag(); context.stroke(); context.beginPath(); context.arc(u(7), u(32), u(3.1), 0, Math.PI * 2); context.stroke();
  // Chequered flag clipped to its waving outline.
  context.save(); flag(); context.clip(); context.fillStyle = '#ffffff'; context.fillRect(u(8), u(1), u(19), u(20));
  context.fillStyle = '#10161b'; const cols = 5; const rowsN = 3; const cw = 18 / cols; const ch = 14 / rowsN;
  for (let r = 0; r < rowsN; r += 1) for (let c = 0; c < cols; c += 1) if ((r + c) % 2 === 0) context.fillRect(u(8 + c * cw), u(3 + r * ch), u(cw + 0.05), u(ch + 0.05));
  context.restore();
  flag(); context.strokeStyle = INK; context.lineWidth = u(0.9); context.stroke();
  // Pole in the object's colour, with a foot ring exactly on the point.
  context.strokeStyle = color; context.lineWidth = u(2.1); context.beginPath(); context.moveTo(u(7), u(31)); context.lineTo(u(7), u(2.5)); context.stroke();
  context.beginPath(); context.arc(u(7), u(2.5), u(1.6), 0, Math.PI * 2); context.fillStyle = color; context.fill();
  context.beginPath(); context.arc(u(7), u(32), u(2.3), 0, Math.PI * 2); context.fillStyle = '#ffffff'; context.fill(); context.strokeStyle = color; context.lineWidth = u(1.5); context.stroke();
};

/** Icon of one of the three symbols at `size` (symbol scale × render scale); null where no 2D canvas exists (tests). */
export function locationIcon(id: LocationSymbolId, color: string, variant: PlanningWellVariant, size = 1, opacity = 1): Icon | null {
  const { width, height, anchor } = LOCATION_SYMBOLS[id];
  const draw = id === 'gps-point' ? drawPin(color) : id === 'farm' ? drawFarm(color, variant) : drawFlag(color);
  return canvasIcon(`${id}|${id === 'farm' ? variant : ''}|${color}`, width, height, size, opacity, draw, anchor);
}
